import React, { useEffect, useMemo, useState } from "react"

import { useSearchParams } from "react-router-dom"

import { ScreenTabsElement } from "@front-shared/elements/screenTabs.element.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook.ts"

import { GENERAL_CONFIG } from "../../config/general.config"
import { MENU_CONFIG } from "../../config/menu.config"
import { FeatureFlagsHelper } from "../../helpers/featureFlags.helper"
import { PermissionsHelper } from "../../helpers/permissions.helper"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import type { IOrganizationAvailableTab } from "../../types/objects.types.ts"
import { CoveragePayersComponent } from "./coveragePayers/coveragePayers.component"
import { OrganizationInformationComponent } from "./organizationInformation.component"
import { OrganizationUsersComponent } from "./users/organizationUsers.component"

export const OrganizationComponent: React.FunctionComponent = () => {
    useScrollFix()

    const [searchParams, setSearchParams] = useSearchParams()

    const runtime = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    // ?tab=1 is supported in url for quick navigation to some tab
    const [selectedTab, setSelectedTab] = useState<IOrganizationAvailableTab>(
        MENU_CONFIG.organizationAvailableTabs[
            searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.tab) &&
            MENU_CONFIG.organizationAvailableTabs[
                +(searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.tab) || 0)
            ]
                ? +(
                      searchParams.get(
                          GENERAL_CONFIG.urlSearchParamsKeys.tab,
                      ) || 0
                  )
                : 0
        ],
    )

    // If tab changes in url make sure to change it in state
    useEffect(() => {
        const tab = searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.tab)

        if (!tab || !MENU_CONFIG.organizationAvailableTabs[+tab]) {
            return
        }

        setSelectedTab(MENU_CONFIG.organizationAvailableTabs[+tab])

        // Clean url
        searchParams.delete(GENERAL_CONFIG.urlSearchParamsKeys.tab)
        setSearchParams(searchParams)
    }, [searchParams])

    const availableTabs = useMemo<IOrganizationAvailableTab[]>(() => {
        if (!selectedPracticeRole) {
            return []
        }

        // Filter out tabs by permissions
        return MENU_CONFIG.organizationAvailableTabs.filter(item => {
            if (!runtime.enabledFeatureFlags) {
                return false
            }

            if (
                item.featureFlag &&
                !FeatureFlagsHelper.isFeatureFlagEnabled(
                    runtime.enabledFeatureFlags,
                    item.featureFlag,
                )
            ) {
                return false
            }

            return (
                !item.neededPermissions ||
                PermissionsHelper.hasPermission(
                    item.neededPermissions,
                    selectedPracticeRole,
                    item.neededPermissionsLogicalOperator,
                )
            )
        })
    }, [selectedPracticeRole?.id, runtime.enabledFeatureFlags])

    const tabRelatedContent = useMemo<JSX.Element | null>(() => {
        switch (selectedTab.key) {
            case "information":
                return <OrganizationInformationComponent />

            case "users":
                return <OrganizationUsersComponent />

            case "payers":
                return <CoveragePayersComponent />

            default:
                return null
        }
    }, [selectedTab.key, availableTabs])

    return (
        <div className="relative md:mx-[16px]">
            {!MENU_CONFIG.organizationTabsHiddenKeys.includes(
                selectedTab.key,
            ) ? (
                availableTabs.length > 1 ? (
                    <ScreenTabsElement
                        className="w-[840px] mx-auto mt-[32px] mb-[40px] md:w-full"
                        currentTab={selectedTab}
                        tabs={availableTabs}
                        onSelected={tab => {
                            setSelectedTab(tab as IOrganizationAvailableTab)
                        }}
                    />
                ) : null
            ) : null}

            {tabRelatedContent}
        </div>
    )
}
